import * as React from 'react';
import { parsePublication, pollDelay, type LivePublication } from '@/lib/realtime';
import { useRealtime } from './RealtimeProvider';

/**
 * The two halves every live screen wires (design 4.2):
 * - `useLiveChannels(channels, onEvent)`: subscribe while mounted, hand over each parsed publication.
 * - `useLiveRefresh(reload, channels)`: publications, a reconnect gap, the poll timer and the tab coming
 *   back all end in `reload()` of the screen's OWN query. Nothing is patched from a payload (a first
 *   subscription recovers nothing, and a stale or duplicate publication must never show a wrong state).
 */

/** The same channel set in another array is not a resubscribe. */
function useStableChannels(channels: string[]): string[] {
  const key = [...channels].sort().join('\n');
  return React.useMemo(() => (key ? key.split('\n') : []), [key]);
}

export function useLiveChannels(
  channels: string[],
  onEvent: (pub: LivePublication, channel: string) => void,
  opts: { onGap?: () => void } = {},
): void {
  const { subscribe } = useRealtime();
  const stable = useStableChannels(channels);
  const eventRef = React.useRef(onEvent);
  eventRef.current = onEvent;
  const gapRef = React.useRef(opts.onGap);
  gapRef.current = opts.onGap;

  React.useEffect(() => {
    const offs = stable.map((channel) =>
      subscribe(channel, {
        onPublication: (data) => {
          const pub = parsePublication(data);
          if (pub) eventRef.current(pub, channel);
        },
        // Every successful subscribe re-reads the gap, except a complete recovery. A FIRST subscribe recovers
        // nothing (no position to recover from), so an event published between the screen's mount fetch and
        // the subscribe completing would otherwise be lost until the safety poll.
        onSubscribed: (ctx) => {
          if (!ctx.wasRecovering || !ctx.recovered) gapRef.current?.();
        },
        onError: () => console.warn('realtime: channel refused or failed', channel),
      }),
    );
    return () => offs.forEach((off) => off());
  }, [stable, subscribe]);
}

export const RELOAD_DEBOUNCE_MS = 400;

function isVisible(): boolean {
  return typeof document === 'undefined' || document.visibilityState !== 'hidden';
}

/** The publication types that re-read a screen when it does not say which: every type except the
 *  position hint (a moving truck sends one about every 10 s; only the Location card re-reads on it), the
 *  team activity hint (activity-feed: one per team action on the user channel; only the bell and the
 *  Activity page read on it, and they ask for it by type) and the load board lead hint (loadboard-leads:
 *  one per lead change to every board user of the agency; only the leads read asks for it by type). */
const QUIET_TYPES: ReadonlySet<LivePublication['type']> = new Set(['load.position', 'activity.new', 'loadboard.lead']);
export function reloadsByDefault(type: LivePublication['type']): boolean {
  return !QUIET_TYPES.has(type);
}

export function useLiveRefresh(
  reload: () => void,
  channels: string[],
  opts: {
    onEvent?: (pub: LivePublication, channel: string) => void;
    /** Only these publication types reload. Absent = every type except `load.position`, `activity.new` and `loadboard.lead`. `onEvent` still
     *  receives every parsed publication. */
    types?: LivePublication['type'][];
  } = {},
): { state: ReturnType<typeof useRealtime>['state'] } {
  const { state } = useRealtime();
  const reloadRef = React.useRef(reload);
  reloadRef.current = reload;
  const onEventRef = React.useRef(opts.onEvent);
  onEventRef.current = opts.onEvent;
  const typesRef = React.useRef(opts.types);
  typesRef.current = opts.types;
  const debounce = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(
    () => () => {
      if (debounce.current) clearTimeout(debounce.current);
    },
    [],
  );

  useLiveChannels(
    channels,
    (pub, channel) => {
      onEventRef.current?.(pub, channel);
      const types = typesRef.current;
      if (types ? !types.includes(pub.type) : !reloadsByDefault(pub.type)) return;
      if (debounce.current) clearTimeout(debounce.current);
      debounce.current = setTimeout(() => {
        debounce.current = null;
        reloadRef.current();
      }, RELOAD_DEBOUNCE_MS);
    },
    { onGap: () => reloadRef.current() },
  );

  // The safety / fallback poll: 120 s live, 20 s otherwise, none while the tab is hidden.
  const [visible, setVisible] = React.useState(isVisible);
  React.useEffect(() => {
    const onChange = () => {
      const now = isVisible();
      setVisible(now);
      if (now) reloadRef.current();
    };
    document.addEventListener('visibilitychange', onChange);
    return () => document.removeEventListener('visibilitychange', onChange);
  }, []);
  const delay = pollDelay(state, visible);
  React.useEffect(() => {
    if (delay === null) return;
    const id = setInterval(() => reloadRef.current(), delay);
    return () => clearInterval(id);
  }, [delay]);

  return { state };
}
